import { createSignal, createMemo, createEffect, For, Show, onMount, onCleanup, untrack, type Accessor } from "solid-js";
import { FiSettings } from "solid-icons/fi";
import { sourcesStore } from "../../../../store/sourcesStore";
import { debug, warn, error as logError } from "../../../../utils/console";
import { apiEndpoints, config } from "@config/env";
import { getData, postData } from "../../../../utils/global";
import LiveChannelPicker from "../../../../components/utilities/LiveChannelPicker";
import { LIVE_CHANNELS } from "../../../../constants/liveChannels";
import { persistantStore } from "../../../../store/persistantStore";
import { user } from "../../../../store/userStore";
import { streamingDataService, type MergedDataPoint } from "../../../../services/streamingDataService";
import { logPageLoad } from "../../../../utils/logging";
import { arrowStreamClient } from "../../../../services/arrowStreamClient";
import { liveColumnarStore } from "../../../../store/liveColumnarStore";
import { LIVE_STREAM_CHANNELS } from "../../../../constants/liveChannels";
import LiveMapStreamStatus from "../../../../components/charts/map/components/LiveMapStreamStatus";
import IconToggle from "../../../../components/utilities/IconToggle";
import {
  filterFocusPoints,
  focusFoilingFilterLabel,
  focusPointsHaveDisplacementData,
  FOCUS_RACE_ALL,
  type FocusPosMode,
} from "../../../../utils/liveTableFocusFilters";

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

/** Focus stats reload interval when auto refresh is enabled. */
const LIVE_TABLE_AUTO_REFRESH_MS = 30 * 1000;

/** Hard-coded focus panel: history channel list (focus stats fetch). */
const FOCUS_CHANNELS = [
  'Tws_kph',
  'Bsp_kph',
  'Twa_n_deg',
  'Vmg_kph',
  'Vmg_perc',
  'Pitch_deg',
  'Heel_n_deg',
  'RH_lwd_mm',
  'DB_cant_lwd_deg',
  'DB_cant_eff_lwd_deg',
  'RUD_diff_ang_deg',
  'DB_rake_ang_lwd_deg',
  'CA1_ang_n_deg',
  'WING_twist_n_deg',
  'WING_clew_pos_n_mm',
  'JIB_sheet_load_kgf',
  'JIB_cunno_load_kgf',
  'JIB_lead_ang_deg'
] as const;

const META_KEYS_FOCUS = new Set(['timestamp', 'Datetime', 'datetime', 'source_name', 'source_id']);

function normChannelKey(s: string): string {
  return s.toLowerCase().replace(/_/g, '');
}

/** Header cell class for source columns; GER gets a distinct highlight via CSS. */
function liveTableSourceHeaderClass(source: string): string {
  return source.trim().toUpperCase() === 'GER'
    ? 'realtime-table-col-source realtime-table-col-source--ger'
    : 'realtime-table-col-source';
}

/** History fetch channel list: required meta + default focus set + any extra from channel picker. */
function focusFetchChannelList(extraChannels: readonly string[] = []): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (c: string) => {
    if (!seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  };
  add('Twa_deg');
  add('Bsp_kph');
  add('GRADE');
  add('Race_number');
  for (const c of FOCUS_CHANNELS) add(c);
  for (const c of extraChannels) add(c);
  return out;
}

function getNumericFromPoint(pt: Record<string, unknown>, channel: string): number | null {
  const n = normChannelKey(channel);
  const direct = pt[channel];
  if (direct !== null && direct !== undefined) {
    const num = typeof direct === 'number' && Number.isFinite(direct) ? direct : Number(direct);
    return Number.isFinite(num) ? num : null;
  }
  for (const k of Object.keys(pt)) {
    if (META_KEYS_FOCUS.has(k)) continue;
    if (normChannelKey(k) !== n) continue;
    // Maneuver review "Grade" (0–5) normalizes like realtime "GRADE"; only the latter is used for quality filter / GRADE channel.
    if (channel === 'GRADE' && k === 'Grade') continue;
    const raw = pt[k];
    if (raw === null || raw === undefined) return null;
    const num = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/** Normalized Race_number for filtering (trimmed string); null if absent. */
function getRaceNumberKeyFromPoint(rec: Record<string, unknown>): string | null {
  const directKeys = ['Race_number', 'race_number', 'RACE', 'RaceNumber', 'raceNumber'];
  for (const dk of directKeys) {
    if (!(dk in rec)) continue;
    const raw = rec[dk];
    if (raw === null || raw === undefined) continue;
    const s = String(raw).trim();
    if (s !== '') return s;
  }
  for (const k of Object.keys(rec)) {
    if (META_KEYS_FOCUS.has(k)) continue;
    if (normChannelKey(k) !== 'racenumber') continue;
    const raw = rec[k];
    if (raw === null || raw === undefined) continue;
    const s = String(raw).trim();
    if (s !== '') return s;
  }
  return null;
}

function compareRaceOptionKeys(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  const aNum = Number.isFinite(na) && String(na) === a;
  const bNum = Number.isFinite(nb) && String(nb) === b;
  if (aNum && bNum) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true });
}

function computeMean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function computeMin(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.min(...values);
}

function computeMax(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.max(...values);
}

function computeSampleStdDev(values: number[]): number | null {
  if (values.length < 2) return null;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const varSum = values.reduce((s, x) => s + (x - mean) ** 2, 0);
  return Math.sqrt(varSum / (values.length - 1));
}

export type FocusStatKind = 'min' | 'max' | 'mean' | 'std';

const FOCUS_STAT_KINDS: readonly FocusStatKind[] = ['mean', 'min', 'max', 'std'] as const;

function isFocusStatKind(value: string): value is FocusStatKind {
  return (FOCUS_STAT_KINDS as readonly string[]).includes(value);
}

function computeFocusStat(values: number[], kind: FocusStatKind): number | null {
  switch (kind) {
    case 'min':
      return computeMin(values);
    case 'max':
      return computeMax(values);
    case 'mean':
      return computeMean(values);
    case 'std':
      return computeSampleStdDev(values);
    default:
      return computeMean(values);
  }
}

function focusStatLabel(kind: FocusStatKind): string {
  switch (kind) {
    case 'min':
      return 'Min';
    case 'max':
      return 'Max';
    case 'mean':
      return 'Mean';
    case 'std':
      return 'Std';
    default:
      return 'Mean';
  }
}

function formatFocusStatValue(value: string | number | null, kind: FocusStatKind): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    if (kind === 'std') return value.toFixed(3);
    if (Number.isInteger(value)) return value.toString();
    return Math.abs(value) < 1 ? value.toFixed(2) : value.toFixed(1);
  }
  return String(value);
}

interface TableRow {
  rowType: 'source_name' | 'channel';
  label: string;
  [sourceName: string]: string | number | null | boolean | 'source_name' | 'channel';
}

interface FocusPosStatsTableProps {
  posLabel: string;
  headingSuffix: Accessor<string>;
  statKind: Accessor<FocusStatKind>;
  rows: Accessor<TableRow[]>;
  scales: Accessor<{ min: number; max: number }[]>;
  sourceColumns: Accessor<string[]>;
  getCellClassForRowValue: (
    value: string | number | null,
    rowIndex: number,
    scales: { min: number; max: number }[]
  ) => string;
}

function FocusPosStatsTable(props: FocusPosStatsTableProps) {
  return (
    <div class="realtime-table-focus-pos-row">
      <div class="realtime-table-focus-full">
        <h3 class="realtime-table-focus-heading">
          {props.posLabel} ({focusStatLabel(props.statKind()).toLowerCase()}){props.headingSuffix()}
        </h3>
        <div class="realtime-table-focus-table-wrap">
          <table class="realtime-data-table realtime-table-focus-table">
            <thead>
              <tr>
                <th class="realtime-table-col-channel">Channel / Source</th>
                <For each={props.sourceColumns()}>
                  {(source) => <th class={liveTableSourceHeaderClass(source)}>{source}</th>}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={props.rows()}>
                {(row, index) => (
                  <tr>
                    <td class="realtime-table-col-channel">{row.label}</td>
                    <For each={props.sourceColumns()}>
                      {(source) => {
                        const value = row[source] as string | number | null;
                        const scaleClass = props.getCellClassForRowValue(
                          value,
                          index(),
                          props.scales()
                        );
                        return (
                          <td class={`realtime-table-cell realtime-table-col-source ${scaleClass}`}>
                            {formatFocusStatValue(value, props.statKind())}
                          </td>
                        );
                      }}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Stored under user object `live` / `live_table` (JSON). */
interface LiveTableUserJson {
  channels?: string[];
  /** When true, low-wind focus stats use BSP >= 28 kph; when false, BSP < 28 kph. */
  focusFoilingEnabled?: boolean;
  /** @deprecated Use focusFoilingEnabled; migrated on load. */
  focusHullMode?: 'foiling' | 'displacement';
  /** Focus race filter: ALL or a Race_number value string from merged data. */
  focusSelectedRaceKey?: string;
  /** Statistic shown in focus tables: min, max, mean, or std. */
  focusStatKind?: FocusStatKind;
  /** History time window (ms) when Race = ALL: last 15 min or last 1 hr. */
  focusWindowMs?: number;
  /** When true, reload focus cache every {@link LIVE_TABLE_AUTO_REFRESH_MS}. */
  liveTableAutoRefresh?: boolean;
  /** Page-local source filter (source_ids). Empty/absent = all sources. Independent of the live-map selection. */
  focusSelectedSourceIds?: number[];
}

/**
 * LiveTable Component — focus statistics only: |TWA| bands per source, configurable channels,
 * Race_number or time window (15 min / 1 hr), GRADE = 3 focus stats with foiling toggle. Source selection follows the live map.
 */
export default function LiveTable() {
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [sources, setSources] = createSignal<string[]>([]);
  const [channels, setChannels] = createSignal<string[]>([]);
  const [displayChannels, setDisplayChannels] = createSignal<string[]>([]);
  const [showChannelPicker, setShowChannelPicker] = createSignal(false);
  const [loadingFocus, setLoadingFocus] = createSignal(false);
  const [focusError, setFocusError] = createSignal<string | null>(null);
  /** When true, low-wind focus stats use BSP >= 28; when false, BSP < 28. High wind always included. */
  const [focusFoilingEnabled, setFocusFoilingEnabled] = createSignal(true);
  /** ALL = no race filter; otherwise match merged point Race_number (string form). */
  const [focusSelectedRace, setFocusSelectedRace] = createSignal<string>(FOCUS_RACE_ALL);
  /** Statistic type for focus tables (default mean). */
  const [focusStatKind, setFocusStatKind] = createSignal<FocusStatKind>('mean');
  const [focusPointsCache, setFocusPointsCache] = createSignal<Map<string, MergedDataPoint[]>>(new Map());
  /** History window when Race = ALL (15 min or 1 hr). */
  const [focusWindowMs, setFocusWindowMs] = createSignal<number>(FIFTEEN_MINUTES_MS);
  /** When true, focus cache reloads on a fixed interval; manual load or Refresh turns this off. */
  const [liveTableAutoRefresh, setLiveTableAutoRefresh] = createSignal(false);
  /**
   * Page-local source filter (source_ids). This is DELIBERATELY independent of the global/live-map source
   * selection (liveSourcesStore) — the source chips across the top of this table control it alone. Empty set
   * means "show all sources". Persisted per class/project in the live_table user object.
   */
  const [pageSelectedSourceIds, setPageSelectedSourceIds] = createSignal<Set<number>>(new Set());

  let isMounted = true;
  /** Ref-counted Arrow-stream consumer key so a co-mounted LiveMap shares the same socket (union subscription). */
  const LIVE_STREAM_CONSUMER = 'live-table';

  const LIVE_TABLE_PARENT = 'live';
  const LIVE_TABLE_OBJECT = 'live_table';

  /** Load saved channel list from live_table user object; apply to displayChannels if present. */
  const loadLiveTableChannels = async (): Promise<void> => {
    const className = persistantStore.selectedClassName?.();
    const projectId = persistantStore.selectedProjectId?.();
    const currentUser = user();
    if (!className || projectId == null || !currentUser?.user_id) return;
    try {
      const url = `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&user_id=${encodeURIComponent(currentUser.user_id)}&parent_name=${encodeURIComponent(LIVE_TABLE_PARENT)}&object_name=${encodeURIComponent(LIVE_TABLE_OBJECT)}`;
      const response = await getData(url);
      if (!isMounted) return;
      if (!response.success || response.data == null) return;
      const data: LiveTableUserJson =
        typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (typeof data.focusFoilingEnabled === 'boolean') {
        setFocusFoilingEnabled(data.focusFoilingEnabled);
      } else if (data.focusHullMode === 'foiling' || data.focusHullMode === 'displacement') {
        setFocusFoilingEnabled(data.focusHullMode === 'foiling');
      }
      if (typeof data.focusSelectedRaceKey === 'string' && data.focusSelectedRaceKey.length > 0) {
        setFocusSelectedRace(data.focusSelectedRaceKey);
      }
      if (typeof data.focusStatKind === 'string' && isFocusStatKind(data.focusStatKind)) {
        setFocusStatKind(data.focusStatKind);
      }
      if (typeof data.focusWindowMs === 'number' && Number.isFinite(data.focusWindowMs) && data.focusWindowMs > 0) {
        if (data.focusWindowMs === FIFTEEN_MINUTES_MS || data.focusWindowMs === ONE_HOUR_MS) {
          setFocusWindowMs(data.focusWindowMs);
        }
      }
      if (typeof data.liveTableAutoRefresh === 'boolean') {
        setLiveTableAutoRefresh(data.liveTableAutoRefresh);
        debug('[LiveTable] Restored liveTableAutoRefresh from user settings', { on: data.liveTableAutoRefresh });
      }
      if (Array.isArray(data.focusSelectedSourceIds)) {
        const ids = data.focusSelectedSourceIds.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
        setPageSelectedSourceIds(new Set(ids));
        debug('[LiveTable] Restored page source filter from user settings', { count: ids.length });
      }
      const saved = data?.channels;
      if (!Array.isArray(saved) || saved.length === 0) return;
      const validSet = new Set(LIVE_CHANNELS);
      const filtered = saved.filter((c) => validSet.has(c));
      if (filtered.length > 0) setDisplayChannels(filtered);
    } catch (err) {
      if (!isMounted) return;
      debug('[LiveTable] Load live_table channels (optional)', err);
    }
  };

  /** All available source_ids (from the current sources() name list) — used for the "select all" collapse. */
  const allAvailableSourceIds = (): number[] =>
    sources()
      .map((name) => sourcesStore.getSourceId(name))
      .filter((id): id is number => id != null);

  /**
   * Toggle a source in the page-local filter (chips across the top). Empty set = "all shown", so the first
   * toggle expands to the full set before removing the clicked source; if a toggle ends up with every source
   * selected, collapse back to empty ("all") for a clean persisted state.
   */
  const togglePageSource = (name: string) => {
    const id = sourcesStore.getSourceId(name);
    if (id == null) return;
    clearLiveTableAutoRefreshIfOn('page_source_filter');
    const next = new Set(pageSelectedSourceIds());
    if (next.size === 0) for (const sid of allAvailableSourceIds()) next.add(sid);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const allIds = allAvailableSourceIds();
    if (allIds.length > 0 && allIds.every((sid) => next.has(sid))) next.clear();
    setPageSelectedSourceIds(next);
    void persistLiveTablePreferences({ focusSelectedSourceIds: Array.from(next) });
    // Tables rebuild automatically: this changes displaySources -> tableSourceColumns, which the focus-stats
    // reload effect tracks and re-fetches for the now-selected sources.
  };

  /** Reset the page-local source filter to "all shown". */
  const clearPageSourceFilter = () => {
    clearLiveTableAutoRefreshIfOn('page_source_filter_clear');
    setPageSelectedSourceIds(new Set<number>());
    void persistLiveTablePreferences({ focusSelectedSourceIds: [] });
  };

  /** True when a source chip should render active (explicitly selected, or nothing selected = all shown). */
  const isPageSourceActive = (name: string): boolean => {
    const sel = pageSelectedSourceIds();
    if (sel.size === 0) return true;
    const id = sourcesStore.getSourceId(name);
    return id != null && sel.has(id);
  };

  /** Save live_table user JSON (channels + focus options). Optional fields override current signals. */
  const persistLiveTablePreferences = async (opts?: {
    channels?: string[];
    focusFoilingEnabled?: boolean;
    focusSelectedRaceKey?: string;
    focusStatKind?: FocusStatKind;
    focusWindowMs?: number;
    liveTableAutoRefresh?: boolean;
    focusSelectedSourceIds?: number[];
  }): Promise<void> => {
    const className = persistantStore.selectedClassName?.();
    const projectId = persistantStore.selectedProjectId?.();
    const currentUser = user();
    if (!className || projectId == null || !currentUser?.user_id) {
      debug('[LiveTable] persistLiveTablePreferences skipped (no class/project/user)');
      return;
    }
    const channelsList = opts?.channels ?? displayChannels();
    const foilingEnabled = opts?.focusFoilingEnabled ?? focusFoilingEnabled();
    const raceKey = opts?.focusSelectedRaceKey ?? focusSelectedRace();
    const statKind = opts?.focusStatKind ?? focusStatKind();
    const windowMs = opts?.focusWindowMs ?? focusWindowMs();
    const autoRefresh = opts?.liveTableAutoRefresh ?? liveTableAutoRefresh();
    const selectedSourceIds = opts?.focusSelectedSourceIds ?? Array.from(pageSelectedSourceIds());
    const body: LiveTableUserJson = {
      channels: channelsList,
      focusFoilingEnabled: foilingEnabled,
      focusSelectedRaceKey: raceKey,
      focusStatKind: statKind,
      focusWindowMs: windowMs,
      liveTableAutoRefresh: autoRefresh,
      focusSelectedSourceIds: selectedSourceIds,
    };
    try {
      const payload = {
        class_name: className,
        project_id: Number(projectId),
        user_id: currentUser.user_id,
        parent_name: LIVE_TABLE_PARENT,
        object_name: LIVE_TABLE_OBJECT,
        json: JSON.stringify(body),
      };
      const response = await postData(`${apiEndpoints.app.users}/object`, payload);
      if (!response.success) warn('[LiveTable] Save live_table preferences failed', response.message);
      else debug('[LiveTable] Saved live_table preferences', { channelCount: channelsList.length });
    } catch (err) {
      warn('[LiveTable] Save live_table preferences error', err);
    }
  };

  /** Save channel list to live_table user object (merges with other live_table JSON fields). */
  const saveLiveTableChannels = async (channelsList: string[]): Promise<void> => {
    await persistLiveTablePreferences({ channels: channelsList });
  };

  // Get available sources from streaming API (handles array or object with .sources / .data)
  const fetchSources = async (): Promise<string[]> => {
    try {
      const url = apiEndpoints.stream.sources;
      const response = await getData(url);

      if (!isMounted) return [];
      if (!response.success || response.data == null) return [];

      const raw = response.data;
      const arr: any[] = Array.isArray(raw)
        ? raw
        : (raw?.sources && Array.isArray(raw.sources)
          ? raw.sources
          : raw?.data && Array.isArray(raw.data)
            ? raw.data
            : []);

      const sourceNames = arr
        .map((s: any) => (typeof s === 'string' ? s : s?.source_name))
        .filter((name: string) => name != null && String(name).trim().length > 0)
        .map((name: string) => String(name).trim());
      return sourceNames.length > 0 ? [...new Set(sourceNames)].sort() : [];
    } catch (err) {
      if (!isMounted) return [];
      warn('[LiveTable] Error fetching sources (expected when streaming unavailable)', err);
      return [];
    }
  };

  // Get available channels from all sources (union)
  // Only returns channels matching pattern like "Vmg_kph" (uppercase, underscore, lowercase)
  /**
   * Fetch a sample data point from the history endpoint to discover channels
   * Fetches merged data for common channels to get the full data object with all channels
   */
  /** Fetch project sources from main app API (same list as map/sidebar). */
  const fetchProjectSources = async (): Promise<string[]> => {
    const className = persistantStore.selectedClassName?.();
    const projectId = persistantStore.selectedProjectId?.();
    if (!className || projectId == null) return [];
    try {
      const url = `${apiEndpoints.app.sources}?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}`;
      const response = await getData(url);
      if (!isMounted || !response.success || !Array.isArray(response.data)) return [];
      return response.data
        .map((s: any) => s?.source_name)
        .filter((n: string) => n != null && String(n).trim().length > 0)
        .map((n: string) => String(n).trim());
    } catch (err) {
      if (isMounted) debug('[LiveTable] fetchProjectSources failed', err);
      return [];
    }
  };

  /** Load sources list only (no WebSocket). Focus stats load when columns are ready. */
  const initializeSources = async () => {
    if (!isMounted) return;
    try {
      setLoading(true);
      setError(null);
      let attempts = 0;
      /** Same cap as Sidebar / Window so we do not block the UI for 10s when sourcesStore is slow. */
      const maxAttempts = 10;
      while (!sourcesStore.isReady() && attempts < maxAttempts && isMounted) {
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }
      if (!isMounted) return;
      await loadLiveTableChannels();
      if (!isMounted) return;
      const [streamSourceNames, projectSourceNames] = await Promise.all([
        fetchSources(),
        fetchProjectSources()
      ]);
      if (!isMounted) return;
      const storeSourceNames = sourcesStore.isReady() ? sourcesStore.sources().map((s) => s.source_name) : [];
      const combined = [...new Set([...projectSourceNames, ...storeSourceNames, ...streamSourceNames])].filter(Boolean);
      if (combined.length === 0) {
        setError('No sources available. Please ensure streaming is active and sources are loaded.');
        return;
      }
      const sourceList = combined.sort();
      setSources(sourceList);
      setChannels([...LIVE_CHANNELS]);
      if (displayChannels().length === 0) setDisplayChannels([...FOCUS_CHANNELS]);
      setLoading(false);
      debug('[LiveTable] Sources loaded (focus table)', { count: sourceList.length, project: projectSourceNames.length, store: storeSourceNames.length, stream: streamSourceNames.length });
    } catch (err) {
      if (!isMounted) return;
      logError('[LiveTable] Error loading sources', err);
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  /** Subtitle when Race = ALL: reflects selected history window. */
  const focusStatsWindowLabel = createMemo((): string => {
    const w = focusWindowMs();
    return w === ONE_HOUR_MS ? 'Last 1 hr' : 'Last 15 min';
  });

  /** Same text as the old line under the header; shown inline next to stream status. */
  const focusHeaderContextLine = createMemo((): string => {
    const filterLabel = focusFoilingFilterLabel(focusFoilingEnabled());
    if (focusSelectedRace() !== FOCUS_RACE_ALL) {
      return `Race ${focusSelectedRace()} · full time range · GRADE = 3 · ${filterLabel}`;
    }
    return `${focusStatsWindowLabel()} · GRADE = 3 · ${filterLabel}`;
  });

  /** Suffix for focus table headings (always grade 3 + current foiling filter). */
  const focusHeadingSuffix = createMemo((): string => {
    return ` · GRADE = 3 · ${focusFoilingFilterLabel(focusFoilingEnabled())}`;
  });

  /** Show foiling toggle only when loaded focus cache includes low-wind displacement samples (GRADE > 1). */
  const focusDisplacementDataAvailable = createMemo((): boolean => {
    const cache = focusPointsCache();
    return focusPointsHaveDisplacementData(cache.values());
  });

  /** Foiling is default; reset when displacement data is not available. */
  createEffect(() => {
    if (!focusDisplacementDataAvailable() && !focusFoilingEnabled()) {
      setFocusFoilingEnabled(true);
    }
  });

  /** Latest live-data timestamp for selected sources (HH:mm:ss) — from the columnar Arrow store, same as the live map. */
  const lastDatetimeLabel = createMemo((): string => {
    if (config.LIVE_MAP_GPU !== true) return '';
    void liveColumnarStore.epoch();
    const ts = liveColumnarStore.latestTs(pageSelectedSourceIds());
    if (ts == null || ts <= 0) return '';
    const d = new Date(ts);
    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  });

  /** Manual data actions turn off timed reload so the user stays in control. */
  const clearLiveTableAutoRefreshIfOn = (reason: string) => {
    if (!liveTableAutoRefresh()) return;
    setLiveTableAutoRefresh(false);
    void persistLiveTablePreferences({ liveTableAutoRefresh: false });
    debug('[LiveTable] Auto refresh turned off', { reason });
  };

  /** Turn on 30s auto reload (persisted). Toolbar: green "Auto refresh" → enables this. */
  const activateLiveTableAutoRefresh = () => {
    if (liveTableAutoRefresh()) return;
    setLiveTableAutoRefresh(true);
    void persistLiveTablePreferences({ liveTableAutoRefresh: true });
    debug('[LiveTable] Live auto refresh enabled', {});
  };

  /**
   * Toolbar: idle = green "Auto refresh" (enables auto). Live = blue "Refresh"
   * (reload focus now, turns auto off).
   */
  const onFocusAutoRefreshToolbarClick = () => {
    if (liveTableAutoRefresh()) {
      void loadFocusStats();
      return;
    }
    activateLiveTableAutoRefresh();
  };

  // Filter sources by live map selection; table columns use this list
  const displaySources = createMemo(() => {
    const allSources = sources();
    const selectedIds = pageSelectedSourceIds();
    if (selectedIds.size === 0) return allSources;
    const selectedNames = new Set(
      Array.from(selectedIds)
        .map((id) => sourcesStore.getSourceName(id))
        .filter((n): n is string => !!n)
    );
    const filtered = allSources.filter((name) => selectedNames.has(name));
    return filtered.length > 0 ? filtered : allSources;
  });

  // Table columns: use displaySources so table stays in sync with live map selection (enabled/disabled sources)
  const tableSourceColumns = createMemo(() => {
    const filtered = displaySources();
    return filtered.length > 0 ? [...filtered] : [];
  });

  /** When channel list is ready and user has no saved selection, default rows to {@link FOCUS_CHANNELS}. */
  createEffect(() => {
    const all = channels();
    const disp = displayChannels();
    if (all.length > 0 && disp.length === 0) {
      setDisplayChannels([...FOCUS_CHANNELS]);
    }
  });

  const channelsToDisplay = createMemo(() => {
    const disp = displayChannels();
    if (disp.length === 0) return [...FOCUS_CHANNELS];
    return disp;
  });

  // Available channels for picker: canonical streaming list + any discovered at runtime
  const availableChannelsForPicker = createMemo(() => {
    const discovered = channels();
    const known = new Set(LIVE_CHANNELS);
    const extra = discovered.filter((c) => !known.has(c));
    return [...LIVE_CHANNELS, ...extra];
  });

  const loadFocusStats = async (opts?: { fromAutoTick?: boolean }) => {
    if (!opts?.fromAutoTick) {
      clearLiveTableAutoRefreshIfOn('manual_focus_stats_load');
    }
    const sourceNames = tableSourceColumns();
    if (sourceNames.length === 0) {
      setFocusError('No sources selected.');
      return;
    }
    setLoadingFocus(true);
    setFocusError(null);
    try {
      const raceSel = focusSelectedRace();
      const raceOnly = raceSel !== FOCUS_RACE_ALL;
      const windowMs = focusWindowMs();
      const endTime = Date.now();
      const startTime = endTime - windowMs;
      const channelList = focusFetchChannelList(channelsToDisplay());
      const map = new Map<string, MergedDataPoint[]>();
      await Promise.all(
        sourceNames.map(async (sourceName) => {
          const pts = raceOnly
            ? await streamingDataService.fetchMergedData(sourceName, channelList, undefined, undefined)
            : await streamingDataService.fetchMergedData(sourceName, channelList, startTime, endTime);
          if (isMounted) map.set(sourceName, pts);
        })
      );
      if (!isMounted) return;
      setFocusPointsCache(new Map(map));
      debug('[LiveTable] Focus stats cache loaded', {
        raceFilter: raceSel,
        timeWindowMs: raceOnly ? null : windowMs,
        fullRangeFetch: raceOnly,
        sourceCount: sourceNames.length,
        pointsPerSource: sourceNames.map((n) => [n, map.get(n)?.length ?? 0])
      });
    } catch (err) {
      if (!isMounted) return;
      logError('[LiveTable] Focus stats load failed', err);
      setFocusError(err instanceof Error ? err.message : 'Failed to load focus stats');
    } finally {
      if (isMounted) setLoadingFocus(false);
    }
  };

  /**
   * Reload focus cache when sources exist and race or GRADE filter changes.
   * Time window (15 min / 1 hr) is applied via explicit button clicks + {@link loadFocusStats} to avoid double fetch and to clear auto-refresh.
   */
  createEffect(() => {
    if (tableSourceColumns().length === 0) return;
    focusSelectedRace();
    focusFoilingEnabled();
    void loadFocusStats({ fromAutoTick: true });
  });

  /**
   * Timed reload every {@link LIVE_TABLE_AUTO_REFRESH_MS}. The tick body must run inside {@link untrack}
   * so signal reads (loading flags, last window, etc.) do not subscribe this effect — otherwise each load
   * completion re-runs the effect, clears the interval, and fires tick() again immediately (constant reload).
   */
  createEffect(() => {
    if (!liveTableAutoRefresh()) return;
    const tick = () => {
      untrack(() => {
        if (!isMounted || loading() || loadingFocus()) return;
        if (tableSourceColumns().length === 0) return;
        void loadFocusStats({ fromAutoTick: true });
      });
    };
    tick();
    const id = window.setInterval(tick, LIVE_TABLE_AUTO_REFRESH_MS);
    onCleanup(() => window.clearInterval(id));
  });

  /**
   * Feed the live badge/clock from the columnar Arrow stream. The legacy JSON WebSocket (streamingStore /
   * streamingService) was retired — arrowStreamClient → liveColumnarStore is the live data path now. This
   * subscribes the page-selected sources (ref-counted union, shared with LiveMap when co-mounted) so freshness
   * works even in a table-only view. Focus-table stats come from the history REST endpoint (loadFocusStats), independent of this.
   */
  createEffect(() => {
    if (!isMounted) return;
    if (config.LIVE_MAP_GPU !== true) return; // legacy JSON WS path is retired; nothing to drive
    if (!config.ENABLE_WEBSOCKETS) {
      debug('[LiveTable] WebSockets disabled — skipping live Arrow subscription');
      return;
    }
    if (!sourcesStore.isReady()) return;

    const sourceIds = pageSelectedSourceIds();
    if (!sourceIds || sourceIds.size === 0) {
      // No explicit selection (= all): drop our subscription; the table still loads stats via REST.
      arrowStreamClient.release(LIVE_STREAM_CONSUMER);
      return;
    }
    const names = Array.from(sourceIds)
      .map((id) => sourcesStore.getSourceName(id))
      .filter((n): n is string => !!n);
    if (names.length === 0) return;
    // Re-calling subscribe with the same consumer id updates its sources; release on cleanup below.
    arrowStreamClient.subscribe(LIVE_STREAM_CONSUMER, names, [...LIVE_STREAM_CHANNELS]);
  });

  /** Distinct Race_number values across all sources in the focus cache (sorted). */
  const focusRaceOptions = createMemo((): string[] => {
    const cache = focusPointsCache();
    const seen = new Set<string>();
    for (const [, pts] of cache) {
      for (const pt of pts) {
        const k = getRaceNumberKeyFromPoint(pt as Record<string, unknown>);
        if (k !== null) seen.add(k);
      }
    }
    return [...seen].sort(compareRaceOptionKeys);
  });

  createEffect(() => {
    const opts = focusRaceOptions();
    const sel = focusSelectedRace();
    if (sel === FOCUS_RACE_ALL) return;
    if (!opts.includes(sel)) {
      setFocusSelectedRace(FOCUS_RACE_ALL);
    }
  });

  /**
   * Single pass over filtered points per source per PoS — returns stat rows and sample windows.
   */
  const buildFocusStats = (mode: FocusPosMode, kind: FocusStatKind): TableRow[] => {
    const sources = tableSourceColumns();
    const cache = focusPointsCache();
    const foilingEnabled = focusFoilingEnabled();
    const raceKey = focusSelectedRace();

    // Filter once per source — shared by both rows and sample windows
    const filteredPerSource = new Map<string, MergedDataPoint[]>();
    for (const src of sources) {
      const pts = cache.get(src) ?? [];
      filteredPerSource.set(
        src,
        filterFocusPoints(pts, mode, foilingEnabled, raceKey, getRaceNumberKeyFromPoint)
      );
    }

    const rows: TableRow[] = [];
    const focusChannelList = channelsToDisplay();
    for (const ch of focusChannelList) {
      const row: TableRow = { rowType: 'channel', label: ch };
      for (const src of sources) {
        const filtered = filteredPerSource.get(src) ?? [];
        const vals: number[] = [];
        for (const pt of filtered) {
          const v = getNumericFromPoint(pt as Record<string, unknown>, ch);
          if (v !== null && Number.isFinite(v)) vals.push(v);
        }
        row[src] = computeFocusStat(vals, kind);
      }
      rows.push(row);
    }

    return rows;
  };

  const focusUpRows = createMemo(() => buildFocusStats('upwind', focusStatKind()));
  const focusReachingRows = createMemo(() => buildFocusStats('reaching', focusStatKind()));
  const focusDownRows = createMemo(() => buildFocusStats('downwind', focusStatKind()));

  const scalesForFocusRows = (rows: TableRow[]) => {
    const sources = tableSourceColumns();
    if (rows.length === 0 || sources.length === 0) return [];
    return rows.map((row) => {
      const values = sources
        .map((src) => row[src])
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
      if (values.length === 0) return { min: 0, max: 0 };
      return { min: Math.min(...values), max: Math.max(...values) };
    });
  };

  const focusUpScales = createMemo(() => scalesForFocusRows(focusUpRows()));
  const focusReachingScales = createMemo(() => scalesForFocusRows(focusReachingRows()));
  const focusDownScales = createMemo(() => scalesForFocusRows(focusDownRows()));

  /** Cell class c0–c6 for value-based formatting (blue = low, red = high) using this row's scale. */
  const getCellClassForRowValue = (
    value: string | number | null,
    rowIndex: number,
    scales: { min: number; max: number }[]
  ): string => {
    if (value == null || typeof value !== 'number' || !Number.isFinite(value)) return '';
    const scale = scales[rowIndex];
    if (!scale || scale.min === scale.max) return scale ? 'c3' : '';
    const x = (value - scale.min) / (scale.max - scale.min) * 6;
    const idx = Math.round(Math.max(0, Math.min(6, x)));
    return `c${idx}`;
  };

  onMount(() => {
    void logPageLoad("LiveTable.tsx", "Live Table", "Loaded");
    debug('[LiveTable] Mounting component');
    initializeSources();
  });

  onCleanup(() => {
    debug('[LiveTable] Unmounting component');
    isMounted = false;
    // Release our ref-counted Arrow subscription; the socket only stops when no other consumer (e.g. LiveMap) holds it.
    try {
      arrowStreamClient.release(LIVE_STREAM_CONSUMER);
    } catch (err) {
      warn('[LiveTable] Error releasing Arrow stream subscription', err);
    }
  });

  return (
    <div class="realtime-table-mount">
      <div class="realtime-table-root">
        <div class="realtime-table-header">
          <div class="realtime-table-header-toolbar-row realtime-table-header-toolbar-row--top">
            <div class="realtime-table-header-toolbar-leading-main">
              <button
                type="button"
                class="settings-icon-container realtime-table-settings-btn"
                onClick={() => setShowChannelPicker(true)}
                aria-label="Configure channels"
                title="Configure channels"
              >
                <FiSettings size={20} />
              </button>
              <h2 class="realtime-table-title">Realtime Data Table</h2>
            </div>
            <div class="realtime-table-header-status-group">
              <LiveMapStreamStatus selectedSourceIds={() => pageSelectedSourceIds()} />
              <Show when={!loading() && !error() && sources().length > 0}>
                <span class="realtime-table-header-focus-context" title={focusHeaderContextLine()}>
                  {focusHeaderContextLine()}
                </span>
              </Show>
              <Show when={lastDatetimeLabel()}>
                <span class="realtime-table-averages-badge" title="Latest merged sample time (selected sources)">
                  {lastDatetimeLabel()}
                </span>
              </Show>
            </div>
            <div class="realtime-table-header-toolbar-trailing">
              <label class="realtime-table-focus-race-field realtime-table-header-race-field">
                <span class="realtime-table-focus-race-field-label">Race_number</span>
                <select
                  class="realtime-table-focus-race-select"
                  value={focusSelectedRace()}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    clearLiveTableAutoRefreshIfOn('race_number_filter');
                    setFocusSelectedRace(v);
                    void persistLiveTablePreferences({ focusSelectedRaceKey: v });
                  }}
                  title="ALL: use the selected time window (15 min or 1 hr). One race: full range for that race. |TWA| band per table; GRADE = 3 with foiling toggle when TWS < 20 kph."
                >
                  <option value={FOCUS_RACE_ALL}>ALL</option>
                  <For each={focusRaceOptions()}>
                    {(race) => <option value={race}>{race}</option>}
                  </For>
                </select>
              </label>
              <label class="realtime-table-focus-race-field realtime-table-header-stat-field">
                <span class="realtime-table-focus-race-field-label">Statistic</span>
                <select
                  class="realtime-table-focus-race-select"
                  value={focusStatKind()}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    if (!isFocusStatKind(v)) return;
                    clearLiveTableAutoRefreshIfOn('focus_stat_kind');
                    setFocusStatKind(v);
                    void persistLiveTablePreferences({ focusStatKind: v });
                  }}
                  title="Statistic computed per channel and source for each point-of-sail table."
                >
                  <For each={[...FOCUS_STAT_KINDS]}>
                    {(kind) => <option value={kind}>{focusStatLabel(kind)}</option>}
                  </For>
                </select>
              </label>
              <button
                type="button"
                class="realtime-table-load-averages-btn"
                classList={{ 'realtime-table-mode-btn-selected': focusWindowMs() === FIFTEEN_MINUTES_MS }}
                onClick={() => {
                  setFocusWindowMs(FIFTEEN_MINUTES_MS);
                  void persistLiveTablePreferences({ focusWindowMs: FIFTEEN_MINUTES_MS });
                  void loadFocusStats();
                }}
                disabled={sources().length === 0 || tableSourceColumns().length === 0}
                title="Load merged samples from the last 15 minutes (when Race = ALL)"
              >
                15 min
              </button>
              <button
                type="button"
                class="realtime-table-load-averages-btn"
                classList={{ 'realtime-table-mode-btn-selected': focusWindowMs() === ONE_HOUR_MS }}
                onClick={() => {
                  setFocusWindowMs(ONE_HOUR_MS);
                  void persistLiveTablePreferences({ focusWindowMs: ONE_HOUR_MS });
                  void loadFocusStats();
                }}
                disabled={sources().length === 0 || tableSourceColumns().length === 0}
                title="Load merged samples from the last hour (when Race = ALL)"
              >
                1 hr
              </button>
              <button
                type="button"
                class="realtime-table-focus-auto-refresh-btn"
                classList={{
                  'realtime-table-focus-auto-refresh-btn--idle': !liveTableAutoRefresh(),
                  'realtime-table-focus-auto-refresh-btn--live': liveTableAutoRefresh(),
                }}
                disabled={
                  tableSourceColumns().length === 0 ||
                  loading() ||
                  (liveTableAutoRefresh() && loadingFocus())
                }
                onClick={() => onFocusAutoRefreshToolbarClick()}
                title={
                  liveTableAutoRefresh()
                    ? 'Reload focus stats now and turn off auto refresh (every 30s).'
                    : 'Turn on auto refresh: reload focus stats every 30 seconds.'
                }
                aria-pressed={liveTableAutoRefresh()}
              >
                {liveTableAutoRefresh()
                  ? loadingFocus()
                    ? 'Loading…'
                    : 'Refresh'
                  : 'Auto refresh'}
              </button>
              <Show when={focusDisplacementDataAvailable()}>
                <IconToggle
                  label="Foiling"
                  checked={focusFoilingEnabled()}
                  disabled={sources().length === 0}
                  showStatus={true}
                  title={
                    focusFoilingEnabled()
                      ? 'Foiling on: GRADE = 3, low wind (TWS < 20 kph) uses BSP ≥ 28 kph. Higher wind includes all grade-3 samples.'
                      : 'Foiling off: GRADE = 3, low wind (TWS < 20 kph) uses BSP < 28 kph. Higher wind includes all grade-3 samples.'
                  }
                  onChange={(v) => {
                    clearLiveTableAutoRefreshIfOn('focus_foiling_toggle');
                    setFocusFoilingEnabled(v);
                    void persistLiveTablePreferences({ focusFoilingEnabled: v });
                  }}
                />
              </Show>
            </div>
            <Show when={!loading() && !error() && sources().length > 0}>
              <div class="realtime-table-header-toolbar-row realtime-table-source-filter-row">
                <span class="realtime-table-source-filter-label">Sources</span>
                <div
                  class="realtime-table-source-chips"
                  role="group"
                  aria-label="Limit the sources shown in this table"
                >
                  <For each={sources()}>
                    {(name) => {
                      const sourceColor = sourcesStore.getSourceColor(name);
                      return (
                        <button
                          type="button"
                          class="realtime-table-source-chip"
                          classList={{ 'realtime-table-source-chip--active': isPageSourceActive(name) }}
                          aria-pressed={isPageSourceActive(name)}
                          style={
                            isPageSourceActive(name) && sourceColor
                              ? `background-color: ${sourceColor}; border-color: ${sourceColor}; color: #fff;`
                              : ''
                          }
                          onClick={() => togglePageSource(name)}
                          title={`Show/hide ${name} in this table (independent of the live-map source selection)`}
                        >
                          {name}
                        </button>
                      );
                    }}
                  </For>
                  <Show when={pageSelectedSourceIds().size > 0}>
                    <button
                      type="button"
                      class="realtime-table-source-chip realtime-table-source-chip--all"
                      onClick={clearPageSourceFilter}
                      title="Show all sources"
                    >
                      All
                    </button>
                  </Show>
                </div>
              </div>
            </Show>
          </div>
        </div>

      <Show when={showChannelPicker()}>
        <LiveChannelPicker
          isOpen={showChannelPicker()}
          onClose={() => setShowChannelPicker(false)}
          availableChannels={availableChannelsForPicker()}
          selectedChannels={displayChannels()}
          onSave={(list) => {
            setDisplayChannels(list);
            setShowChannelPicker(false);
            void saveLiveTableChannels(list);
            void loadFocusStats();
          }}
        />
      </Show>

      <Show when={loading()}>
        <div class="realtime-table-state-message realtime-table-state-message--loading">
          Loading sources...
        </div>
      </Show>

      <Show when={!loading() && error()}>
        <div class="realtime-table-state-message realtime-table-state-message--error" role="alert">
          Error: {error()}
        </div>
      </Show>

      <Show when={!loading() && !error() && sources().length > 0}>
          <section
            class="realtime-table-focus-panel realtime-table-focus-panel--solo realtime-tables-scroll-host"
            aria-label="Focus point statistics"
          >
            <Show when={focusError()}>
              <p class="realtime-table-focus-error" role="alert">
                {focusError()}
              </p>
            </Show>
            <Show when={!focusError()}>
              <div
                class="realtime-table-focus-body-wrap"
                classList={{ 'realtime-table-focus-body--loading': loadingFocus() }}
              >
                <Show when={loadingFocus()}>
                  <div class="realtime-table-focus-loading-overlay" aria-live="polite">
                    Updating…
                  </div>
                </Show>
                <div class="realtime-table-focus-stack">
                  <FocusPosStatsTable
                    posLabel="Upwind"
                    headingSuffix={focusHeadingSuffix}
                    statKind={focusStatKind}
                    rows={focusUpRows}
                    scales={focusUpScales}
                    sourceColumns={tableSourceColumns}
                    getCellClassForRowValue={getCellClassForRowValue}
                  />
                  <FocusPosStatsTable
                    posLabel="Reaching"
                    headingSuffix={focusHeadingSuffix}
                    statKind={focusStatKind}
                    rows={focusReachingRows}
                    scales={focusReachingScales}
                    sourceColumns={tableSourceColumns}
                    getCellClassForRowValue={getCellClassForRowValue}
                  />
                  <FocusPosStatsTable
                    posLabel="Downwind"
                    headingSuffix={focusHeadingSuffix}
                    statKind={focusStatKind}
                    rows={focusDownRows}
                    scales={focusDownScales}
                    sourceColumns={tableSourceColumns}
                    getCellClassForRowValue={getCellClassForRowValue}
                  />
                </div>
              </div>
            </Show>
          </section>
      </Show>

      <Show when={!loading() && !error() && tableSourceColumns().length === 0}>
        <div class="realtime-table-state-message realtime-table-state-message--muted">
          No sources available. Please ensure streaming is active.
        </div>
      </Show>
      </div>
    </div>
  );
}
